import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { Subscription } from 'rxjs'
import { Portfolio } from 'src/app/features/portfolio/portfolio.model'
import { FixedIncomeCharacteristic } from 'src/app/models/characteristic-response.model'
import { Footnote } from 'src/app/models/footnote.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'

@Component({
  selector: 'fixed-income-characteristic',
  templateUrl: './fixed-income-characteristic.component.html',
  styleUrls: ['./fixed-income-characteristic.component.scss'],
  providers: [],
})
export class FixedIncomeCharacteristicComponent implements OnInit, OnDestroy {
  @ViewChild('simpleDialogBodyContent')
  simpleDialogBodyContent: TemplateRef<any>
  @ViewChild('averageMaturityDialogBodyContent')
  averageMaturityDialogBodyContent: TemplateRef<any>
  @ViewChild('dialogBodyContainer', { read: ViewContainerRef })
  dialogBodyContainer: ViewContainerRef

  @Input() portfolioModel: Portfolio
  @Input() fundId: string
  @Input() benchmarkFootNote: Footnote
  @Input() portfolioFootNote: Footnote
  fixedIncomeCharacteristic: FixedIncomeCharacteristic
  noCharacteristicsData = false

  dialogTitle: string
  dialogBody: string

  //AEM content variables
  yieldToMaturity: AEMChunk
  averageEffectiveMaturity: AEMChunk
  averageStatedMaturity: AEMChunk
  averageMaturity: AEMChunk
  averageDuration: AEMChunk
  alternativeMinimumTax: AEMChunk
  aemContentSub: Subscription

  constructor(
    private readonly viewContainer: ViewContainerRef,
    private readonly centralContentService: CentralContentService,
  ) {}

  // eslint-disable-next-line complexity
  ngOnInit(): void {
    this.fixedIncomeCharacteristic =
      this.portfolioModel !== null &&
      this.portfolioModel !== undefined &&
      this.portfolioModel.characteristic !== null &&
      this.portfolioModel.characteristic !== undefined &&
      this.portfolioModel.characteristic.fixedIncomeCharacteristic

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.noCharacteristicsData =
      Object.keys(this.fixedIncomeCharacteristic).length === 0 ? true : false

    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    this.yieldToMaturity = this.centralContentService.getContentByBody(
      'fund_definitions_yield_to_maturity',
      content,
    )
    this.averageEffectiveMaturity = this.centralContentService.getContentByBody(
      'fund_definitions_average_effective_maturity',
      content,
    )
    this.averageStatedMaturity = this.centralContentService.getContentByBody(
      'fund_definitions_average_stated_maturity',
      content,
    )
    this.averageMaturity = this.centralContentService.getContentByBody(
      'fund_definitions_average_maturity',
      content,
    )
    this.averageDuration = this.centralContentService.getContentByBody(
      'fund_definitions_average_duration',
      content,
    )
    this.alternativeMinimumTax = this.centralContentService.getContentByBody(
      'fund_definitions_alternative_minimum_tax',
      content,
    )
  }

  /**
   * This method is meant to be used in the HTML click event to set
   * both the dialog title and body before opening. This method decides between two
   * different templates (ng-template / TemplateRef) to use for the body structure of the modal.
   * @param title Title to be used for the modal dialog heading text
   */
  public setDialogInfo(title: string) {
    this.dialogTitle = title
    this.dialogBodyContainer.clear()
    if (title === 'Average Maturity (money market funds only)') {
      this.dialogTitle = 'Average Maturity'
      this.dialogBodyContainer.createEmbeddedView(
        this.averageMaturityDialogBodyContent,
      )
    } else {
      this.setDialogBody(title)
      this.dialogBodyContainer.createEmbeddedView(this.simpleDialogBodyContent)
    }
  }

  /**
   * Not to be confused with setDialogInfo, this is a helper method that sets the body content
   * (this.dialogBody) if we are using the simpleDialogBodyContent template.
   * @param title Title to be used for the modal dialog heading text
   */
  private setDialogBody(title: string) {
    const titleContentMap = {
      'Yield to maturity': this.yieldToMaturity?.contentValue,
      'Average Effective Maturity (taxable bond funds and balanced funds except tax-managed balanced)':
        this.averageEffectiveMaturity?.contentValue,
      'Average Stated Maturity (municipal bond funds and tax-managed balanced)':
        this.averageStatedMaturity?.contentValue,
      Duration: this.averageDuration?.contentValue,
      'Alternative minimum tax (AMT)': this.alternativeMinimumTax?.contentValue,
    }

    this.dialogBody = titleContentMap[title]
  }

  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
